import { Checkbox } from "@/components/ui/checkbox";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useDataTableSettings } from "../hooks/useDataTableSettings";
import { useRowSelectionContext } from "../context/RowSelectionContext";

interface SelectAllHeaderProps {
  rowIds?: string[];
}

const SelectAllHeaderComponent = ({ rowIds }: SelectAllHeaderProps) => {
  const { store } = useRowSelectionContext();
  const settings = useDataTableSettings();

  const tableRows = useMemo(() => {
    return settings?.table.getRowModel().rows ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rowsCount triggers recalculation when table data changes
  }, [settings, settings?.rowsCount]);

  const allRowsIds = useMemo(() => {
    if (rowIds) {
      return rowIds;
    }
    return tableRows.map((row) => row.id) ?? [];
  }, [rowIds, tableRows]);

  const isAllSelected = useSyncExternalStore(
    store.subscribe,
    () => {
      if (allRowsIds.length === 0) {
        return false;
      }
      const selection = store.getSelection();
      return allRowsIds.every((id) => selection.has(id));
    },
    () => false,
  );

  const isSomeSelected = useSyncExternalStore(
    store.subscribe,
    () => {
      if (allRowsIds.length === 0) {
        return false;
      }
      const selection = store.getSelection();
      const selectedCount = allRowsIds.filter((id) => selection.has(id)).length;
      return selectedCount > 0 && selectedCount < allRowsIds.length;
    },
    () => false,
  );

  const handleToggleAll = useCallback(() => {
    store.toggleMany(allRowsIds);
  }, [store, allRowsIds]);

  return (
    <div className="flex w-fit h-full items-center justify-center p-0 mx-auto">
      <Checkbox
        checked={isAllSelected || (isSomeSelected && "indeterminate")}
        onCheckedChange={handleToggleAll}
      />
    </div>
  );
};

export const SelectAllHeader = SelectAllHeaderComponent;
